import { useState } from 'react';
import Breadcrumb from '@/components/base/Breadcrumb';
import { testimonials, faqData } from '@/mocks/homepage';

export default function About() {
  return (
    <div className="flex flex-col min-h-screen">
      <main>
        <BrandSection />
        <ValuesSection />
        <MilestonesSection />
        <TestimonialsSection />
        <FAQSection />
        <ContactSection />
      </main>

      <script type="application/ld+json">{JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        name: '关于btbxx',
        description: '了解btbxx品牌故事、核心价值观、发展历程和联系方式。btbxx是值得信赖的全方位专业企业服务平台。',
        url: 'https://www.btbxx.com/about',
        isPartOf: { '@type': 'WebSite', name: 'btbxx', url: 'https://www.btbxx.com/' },
      })}</script>
    </div>
  );
}

function BrandSection() {
  return (
    <section className="pt-24 pb-12 md:pt-32 md:pb-20 bg-background-100">
      <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
        <div className="mb-6">
          <Breadcrumb items={[
            { label: '首页', path: '/' },
            { label: '关于我们' },
          ]} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 items-center">
          <div>
            <span className="inline-block px-4 py-1.5 rounded-full bg-primary-100 text-primary-700 text-xs font-label font-medium tracking-wide mb-4">
              品牌介绍
            </span>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-6">
              关于 btbxx
            </h1>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-5 font-body">
              btbxx 成立于2020年，是一家专注于为企业提供全方位数字化解决方案的专业服务平台。我们致力于通过先进的技术和优质的服务，帮助企业实现数字化转型和业务增长。
            </p>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-5 font-body">
              经过多年的发展，btbxx已经成长为行业领先的企业服务平台，累计服务超过10,000家企业客户，覆盖制造、零售、教育、金融、医疗等多个行业。我们的团队由200多名技术专家和行业顾问组成，分布在北京、上海、深圳、杭州等多个城市。
            </p>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed font-body">
              btbxx始终秉持"以客户为中心，以技术为驱动"的核心理念，持续投入研发创新，不断优化产品和服务体系。我们相信，通过科技的力量，每一家企业都能获得更高效、更智能的运营能力。
            </p>
          </div>

          <div className="rounded-lg overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20collaborative%20office%20space%20with%20diverse%20professional%20team%20working%20together%2C%20warm%20terracotta%20and%20cream%20interior%20design%2C%20large%20windows%20with%20natural%20light%2C%20clean%20minimal%20workspace%2C%20professional%20corporate%20atmosphere%2C%20modern%20office%20photography&width=1200&height=900&seq=btbxx-about-1&orientation=landscape"
              alt="btbxx团队 - 专业企业服务平台"
              title="btbxx 专业团队"
              className="w-full h-auto object-cover"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

function ValuesSection() {
  const values = [
    {
      icon: 'ri-star-line',
      title: '客户至上',
      desc: '始终将客户需求放在首位，以客户满意度作为衡量成功的核心标准，持续优化服务体验。',
    },
    {
      icon: 'ri-lightbulb-line',
      title: '创新驱动',
      desc: '持续投入技术研发，探索前沿科技应用，以创新思维解决企业数字化转型中的各种挑战。',
    },
    {
      icon: 'ri-shield-check-line',
      title: '安全可靠',
      desc: '将数据安全视为生命线，建立完善的安全防护体系，保障客户数据的安全性和合规性。',
    },
    {
      icon: 'ri-team-line',
      title: '协作共赢',
      desc: '与客户、合作伙伴建立深度合作关系，共同成长，实现多方价值的最大化。',
    },
  ];

  return (
    <section className="py-12 md:py-20 bg-background-50">
      <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10 md:mb-14">
          <span className="inline-block px-4 py-1.5 rounded-full bg-accent-100 text-accent-700 text-xs font-label font-medium tracking-wide mb-4">
            核心价值观
          </span>
          <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950">
            我们的价值观
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {values.map((value) => (
            <div
              key={value.title}
              className="group bg-background-50 rounded-lg p-6 md:p-7 border border-background-200/60 hover:border-accent-200/60 transition-all duration-300 text-center"
            >
              <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-5 group-hover:bg-accent-200 transition-colors duration-300">
                <i className={`${value.icon} text-2xl`}></i>
              </div>
              <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-3">{value.title}</h3>
              <p className="text-sm text-foreground-600 leading-relaxed font-body">{value.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function MilestonesSection() {
  const milestones = [
    { year: '2020', title: 'btbxx正式成立', desc: '核心团队组建完成，获得首轮融资，平台1.0版本上线' },
    { year: '2021', title: '快速成长', desc: '服务客户突破1,000家，完成A轮融资，推出企业解决方案2.0' },
    { year: '2022', title: '产品矩阵完善', desc: '上线技术平台、营销增长、数据智能三大核心产品线' },
    { year: '2023', title: '行业领先', desc: '累计服务企业超5,000家，团队规模突破150人，获得行业多项大奖' },
    { year: '2024', title: '生态布局', desc: '发布开放平台战略，建立合作伙伴生态，服务企业突破8,000家' },
    { year: '2025', title: '全新里程碑', desc: '平台3.0版本发布，AI能力全面升级，累计服务企业超10,000家' },
  ];

  return (
    <section className="py-12 md:py-20 bg-background-100">
      <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10 md:mb-14">
          <span className="inline-block px-4 py-1.5 rounded-full bg-secondary-100 text-secondary-700 text-xs font-label font-medium tracking-wide mb-4">
            发展历程
          </span>
          <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950">
            btbxx 成长之路
          </h2>
        </div>

        <div className="relative">
          <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300 hidden md:block"></div>

          <div className="space-y-8 md:space-y-12">
            {milestones.map((milestone, index) => (
              <div
                key={milestone.year}
                className={`relative flex flex-col md:flex-row gap-4 md:gap-8 ${
                  index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                }`}
              >
                <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                  <div className="bg-background-50 rounded-lg p-5 md:p-6 border border-background-200/60">
                    <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-label font-semibold mb-3">
                      {milestone.year}
                    </span>
                    <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-2">{milestone.title}</h3>
                    <p className="text-sm text-foreground-600 font-body">{milestone.desc}</p>
                  </div>
                </div>

                <div className="hidden md:flex items-center justify-center flex-shrink-0">
                  <div className="w-4 h-4 rounded-full bg-accent-500 border-4 border-background-100 relative z-10"></div>
                </div>

                <div className="flex-1 hidden md:block"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function TestimonialsSection() {
  return (
    <section className="py-12 md:py-20 bg-background-50">
      <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10 md:mb-14">
          <span className="inline-block px-4 py-1.5 rounded-full bg-accent-100 text-accent-700 text-xs font-label font-medium tracking-wide mb-4">
            客户口碑
          </span>
          <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950">
            信誉与评价
          </h2>
          <p className="text-sm text-foreground-600 mt-3 font-body">
            btbxx的信誉源于每一位客户的真实认可
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-8">
          {testimonials.map((item) => (
            <div
              key={item.id}
              className="bg-background-50 rounded-lg p-6 md:p-8 border border-background-200/60"
            >
              <div className="flex items-center gap-1 mb-4">
                {Array.from({ length: item.rating }).map((_, i) => (
                  <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                ))}
              </div>
              <p className="text-sm text-foreground-700 leading-relaxed mb-6 font-body">
                "{item.content}"
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                  <span className="text-primary-700 font-label font-semibold text-sm">
                    {item.name.charAt(0)}
                  </span>
                </div>
                <div>
                  <p className="text-sm font-label font-semibold text-foreground-950">{item.name}</p>
                  <p className="text-xs text-foreground-500 font-label">{item.role} · {item.company}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  return (
    <section className="py-12 md:py-20 bg-background-100">
      <div className="w-full max-w-4xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10 md:mb-14">
          <span className="inline-block px-4 py-1.5 rounded-full bg-secondary-100 text-secondary-700 text-xs font-label font-medium tracking-wide mb-4">
            常见问题
          </span>
          <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950">
            您可能关心的问题
          </h2>
        </div>

        <div className="space-y-3">
          {faqData.map((faq, index) => (
            <details key={index} className="group bg-background-50 rounded-lg border border-background-200/60 overflow-hidden">
              <summary className="flex items-center justify-between px-5 md:px-7 py-5 cursor-pointer list-none [&::-webkit-details-marker]:hidden">
                <span className="text-sm md:text-base font-label font-medium text-foreground-900 pr-6">
                  {faq.question}
                </span>
                <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 group-open:bg-primary-100 group-open:text-primary-600 transition-all duration-200">
                  <i className="ri-add-line text-sm group-open:hidden"></i>
                  <i className="ri-subtract-line text-sm hidden group-open:block"></i>
                </span>
              </summary>
              <div className="px-5 md:px-7 pb-5">
                <p className="text-sm text-foreground-600 leading-relaxed font-body">
                  {faq.answer}
                </p>
              </div>
            </details>
          ))}
        </div>

        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: faqData.map((faq) => ({
            '@type': 'Question',
            name: faq.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: faq.answer,
            },
          })),
        })}</script>
      </div>
    </section>
  );
}

function ContactSection() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt');
    if (honeypot && typeof honeypot === 'string' && honeypot.trim() !== '') {
      setFormStatus('success');
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9q571vnahb7382ov6cg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        if (serverMsg && (serverMsg.includes('spam') || serverMsg.includes('form data is spam'))) {
          setFormStatus('success');
          form.reset();
        } else {
          setFormStatus('error');
          setFormError(serverMsg || '提交失败，请稍后重试');
        }
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查连接后重试');
    }
  };

  return (
    <section id="contact" className="py-12 md:py-20 bg-background-50">
      <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10 md:mb-14">
          <span className="inline-block px-4 py-1.5 rounded-full bg-primary-100 text-primary-700 text-xs font-label font-medium tracking-wide mb-4">
            联系我们
          </span>
          <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-3">
            与 btbxx 取得联系
          </h2>
          <p className="text-sm text-foreground-600 font-body max-w-xl mx-auto">
            无论您有任何问题或合作意向，我们的团队都随时准备为您提供帮助
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
          <div className="space-y-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                <i className="ri-map-pin-line text-xl"></i>
              </div>
              <div>
                <h3 className="font-heading text-base font-semibold text-foreground-950 mb-1">公司地址</h3>
                <p className="text-sm text-foreground-600 font-body">
                  北京市朝阳区建国路88号SOHO现代城A座18层
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                <i className="ri-phone-line text-xl"></i>
              </div>
              <div>
                <h3 className="font-heading text-base font-semibold text-foreground-950 mb-1">联系电话</h3>
                <p className="text-sm text-foreground-600 font-body">
                  400-888-8888
                </p>
                <p className="text-xs text-foreground-500 font-body mt-1">
                  周一至周五 9:00 - 18:00
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                <i className="ri-mail-line text-xl"></i>
              </div>
              <div>
                <h3 className="font-heading text-base font-semibold text-foreground-950 mb-1">电子邮箱</h3>
                <p className="text-sm text-foreground-600 font-body">
                  contact@btbxx.com
                </p>
                <p className="text-xs text-foreground-500 font-body mt-1">
                  我们会在24小时内回复您的邮件
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                <i className="ri-wechat-line text-xl"></i>
              </div>
              <div>
                <h3 className="font-heading text-base font-semibold text-foreground-950 mb-1">官方微信</h3>
                <p className="text-sm text-foreground-600 font-body">
                  搜索关注 "btbxx官方" 公众号
                </p>
                <p className="text-xs text-foreground-500 font-body mt-1">
                  获取最新产品动态和行业资讯
                </p>
              </div>
            </div>
          </div>

          <div className="bg-background-100 rounded-lg p-6 md:p-8 border border-background-200/60">
            <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-5">
              发送消息
            </h3>

            {formStatus === 'success' && (
              <div className="mb-5 p-4 rounded-md bg-accent-50 border border-accent-200 text-accent-800 text-sm font-body">
                <i className="ri-checkbox-circle-line mr-2"></i>
                感谢您的留言！我们会在24小时内与您联系。
              </div>
            )}

            {formStatus === 'error' && (
              <div className="mb-5 p-4 rounded-md bg-red-50 border border-red-200 text-red-800 text-sm font-body">
                <i className="ri-error-warning-line mr-2"></i>
                {formError || '提交失败，请稍后重试'}
              </div>
            )}

            <form
              className="space-y-4"
              onSubmit={handleSubmit}
              data-readdy-form
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="contact-name" className="block text-xs font-label font-medium text-foreground-700 mb-1.5">
                    姓名 <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="contact-name"
                    type="text"
                    name="name"
                    required
                    className="w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors font-body"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-xs font-label font-medium text-foreground-700 mb-1.5">
                    邮箱 <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    name="email"
                    required
                    className="w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors font-body"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="contact-company" className="block text-xs font-label font-medium text-foreground-700 mb-1.5">
                  公司名称
                </label>
                <input
                  id="contact-company"
                  type="text"
                  name="company"
                  className="w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors font-body"
                  placeholder="请输入您的公司名称"
                />
              </div>
              <div>
                <label htmlFor="contact-message" className="block text-xs font-label font-medium text-foreground-700 mb-1.5">
                  留言内容 <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  rows={4}
                  maxLength={500}
                  className="w-full px-3 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none font-body"
                  placeholder="请输入您的问题或需求描述（500字以内）"
                />
              </div>
              <input
                type="text"
                name="phone_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="honeypot-field"
              />
              <button
                type="submit"
                disabled={formStatus === 'submitting'}
                className="w-full px-5 py-3 rounded-md bg-primary-500 text-background-50 font-label font-semibold text-sm hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {formStatus === 'submitting' ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    发送中...
                  </>
                ) : (
                  <>
                    发送消息
                    <i className="ri-send-plane-line ml-2"></i>
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}